import {
  Accordion,
  Table,
  Loader,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { memo, useState, useEffect } from "react";
import { useParams } from "react-router";
import {
  GetTransactionByClientQuery,
  PaymentMethodType,
  TransactionStatus,
  useGetTransactionByClientLazyQuery,
} from "src/generated/graphql";
import { formatter } from "src/utils/constants";
import ScheduledTransactionHistory from "./ScheduledTransactions";

type ITransactionData = GetTransactionByClientQuery["transactionByClient"];

interface FilteredData {
  pending: ITransactionData;
  last7Days: ITransactionData;
  last30Days: ITransactionData;
  olderData: ITransactionData;
}

export const columnsTransaction = [
  "Date",
  "Amount",
  "Location",
  "Status",
  "Method",
];

function filterDataByDays(data: ITransactionData) {
  const result: FilteredData = {
    pending: [],
    last7Days: [],
    last30Days: [],
    olderData: [],
  };
  var today = new Date();

  // Calculate the cutoff dates
  var cutoffDate7Days = new Date();
  cutoffDate7Days.setDate(today.getDate() - 6);

  var cutoffDate30Days = new Date();
  cutoffDate30Days.setDate(today.getDate() - 30);

  // Filter the dataArray based on the date
  data.forEach(function (item) {
    var itemDate = new Date(item.createdDate);
    if (item.transactionStatus === TransactionStatus.Pending) {
      result.pending.push(item);
    } else {
      if (itemDate >= cutoffDate7Days) {
        result.last7Days.push(item);
      } else if (itemDate >= cutoffDate30Days && itemDate < cutoffDate7Days) {
        result.last30Days.push(item);
      } else {
        result.olderData.push(item);
      }
    }
  });
  return result;
}

export const HistoryTableTransaction = (transactions: ITransactionData) => {
  const theme = useMantineTheme();
  return (
    <Table
      sx={{
        thead: {
          tr: {
            th: {
              color: theme.colors.secondaryTextColor[6],
            },
          },
        },
        tbody: {
          tr: {
            "&:nth-of-type(odd)": {
              color: theme.colors.secondaryTextColor[6],
              backgroundColor: theme.colors.secondary[6],
            },
            "&:nth-of-type(even)": {
              color: theme.colors.secondaryTextColor[4],
              backgroundColor: theme.colors.secondary[4],
            },
            td: {
              borderTop: `1px solid ${theme.colors.secondary[2]}`,
            },
          },
        },
      }}
    >
      <thead>
        <tr>
          {columnsTransaction.map((column) => (
            <th key={column}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {transactions?.map((transaction) => (
          <tr key={transaction.id}>
            <td>{new Date(transaction.createdDate).toDateString()}</td>
            <td>{formatter.format(transaction.amount)}</td>
            <td>{transaction.location.name}</td>
            <td>{transaction.transactionStatus}</td>
            <td>
              {transaction.paymentMethod
                ? `${
                    transaction.paymentMethod.type === PaymentMethodType.Card
                      ? transaction.paymentMethod.merchant || "Card"
                      : "Bank"
                  } - ${transaction.paymentMethod.mask?.substring(
                    transaction.paymentMethod.mask.length - 4
                  )}`
                : transaction.paymentTerminal
                ? `Terminal - ${transaction.paymentTerminal.name}`
                : "Cash"}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const TransactionHistory = () => {
  const { clientId } = useParams<{
    clientId: string;
  }>();
  const theme = useMantineTheme();
  const [transactionsDetails, setTransactionsDetails] = useState<FilteredData>({
    pending: [],
    last7Days: [],
    last30Days: [],
    olderData: [],
  });

  const [getTransactions, { loading }] = useGetTransactionByClientLazyQuery({
    onCompleted: (data) => {
      setTransactionsDetails(filterDataByDays(data?.transactionByClient));
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    clientId !== undefined && getTransactions({ variables: { clientId } });
  }, [clientId]);

  return (
    <>
      <Title
        order={4}
        style={{
          paddingBottom: "20px",
          borderBottom: "1px solid",
          color: theme.colors.secondaryTextColor[2],
        }}
      >
        Transaction History
      </Title>
      <Accordion
        multiple
        defaultValue={["recent", "upcomming"]}
        transitionDuration={500}
        styles={{
          item: {
            border: `1px solid ${theme.colors.primary[6]}`,
            marginTop: 15,
            borderRadius: "4px",
            overflow: "hidden",
          },
          control: {
            background: theme.colors.primary[6],
            color: theme.colors.primaryTextColor[6],
            padding: "5px 13px",
            "&:hover": {
              backgroundColor: `${theme.colors.primary[4]} !important`,
            },
          },
          label: {
            fontWeight: 500,
            fontSize: 16,
          },
          panel: {
            background: theme.colors.secondary[6],
          },
        }}
      >
        <Accordion.Item value="scheduled">
          <Accordion.Control>Scheduled</Accordion.Control>
          <Accordion.Panel>
            <ScheduledTransactionHistory clientId={clientId} />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="upcomming">
          <Accordion.Control>Pending</Accordion.Control>
          <Accordion.Panel>
            {loading ? (
              <Loader />
            ) : (
              HistoryTableTransaction(transactionsDetails.pending)
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="recent">
          <Accordion.Control>Recent</Accordion.Control>
          <Accordion.Panel>
            {loading ? (
              <Loader />
            ) : (
              HistoryTableTransaction(transactionsDetails.last7Days)
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="last30Days">
          <Accordion.Control>Last 30 days</Accordion.Control>
          <Accordion.Panel>
            {loading ? (
              <Loader />
            ) : (
              HistoryTableTransaction(transactionsDetails.last30Days)
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="older">
          <Accordion.Control>Older</Accordion.Control>
          <Accordion.Panel>
            {loading ? (
              <Loader />
            ) : (
              HistoryTableTransaction(transactionsDetails.olderData)
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

export default memo(TransactionHistory);
